import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';

import { layoutText, type OcrWord } from '@fpc/shared';

import type { OCRPageResult, OCRResult } from '../types/ocr.types';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// Guide §15 "File Size" — initial limits, to be tuned after performance testing.
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_PDF_BYTES = 25 * 1024 * 1024;
export const MAX_PDF_PAGES = 20;

// Guide §15 "Supported Formats" / "Security" — MIME type and extension must agree.
const SUPPORTED_EXTENSIONS: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

const MB = 1024 * 1024;

/** A page whose text layer has fewer readable characters than this is treated as a scan. */
const MIN_TEXT_LAYER_CHARS = 20;

async function createOCRWorker() {
  return createWorker('eng');
}

type OCRWorker = Awaited<ReturnType<typeof createOCRWorker>>;

/** OCR of one image, with every word's position kept so the page can be laid out as printed. */
async function recognizePage(
  worker: OCRWorker,
  image: File | HTMLCanvasElement,
  page: number,
): Promise<OCRPageResult> {
  const result = await worker.recognize(image, {}, { text: true, blocks: true });
  const words: OcrWord[] = (result.data.blocks ?? []).flatMap((block) =>
    block.paragraphs.flatMap((paragraph) =>
      paragraph.lines.flatMap((line) =>
        line.words.map((word) => ({ text: word.text, ...word.bbox })),
      ),
    ),
  );
  return {
    page,
    text: result.data.text,
    confidence: result.data.confidence,
    layout: layoutText(words),
    source: 'OCR',
  };
}

/**
 * A digital PDF already carries its exact text and where each piece sits. Reading
 * that is both faster and more accurate than OCR of the rendered page; only a
 * page without it (a scan) needs OCR.
 */
async function readTextLayer(
  page: pdfjsLib.PDFPageProxy,
  viewport: pdfjsLib.PageViewport,
  pageNumber: number,
): Promise<OCRPageResult | null> {
  const content = await page.getTextContent();
  const pieces = joinTouching(
    content.items.filter((item): item is TextItem => 'str' in item),
    viewport,
  );
  const words = pieces.filter((piece) => piece.text.trim());
  const readable = words.reduce((sum, word) => sum + word.text.replace(/\s/g, '').length, 0);
  if (readable < MIN_TEXT_LAYER_CHARS) return null;

  return {
    page: pageNumber,
    text: pieces
      .map((piece) => piece.text + (piece.eol ? '\n' : ' '))
      .join('')
      .trim(),
    confidence: 100,
    layout: layoutText(words),
    source: 'TEXT_LAYER',
  };
}

/** How close two text items must sit, as a share of the font size, to be one word. */
const TOUCHING = 0.15;

/**
 * A glyph the PDF's font maps to no character, which pdf.js reports as U+0000.
 * Stripe-style invoices print their hyphens, dashes and brackets this way.
 */
// eslint-disable-next-line no-control-regex -- matching control characters is the point.
const UNMAPPED = /[\u0000-\u0008\u000e-\u001f�]+/g;

// eslint-disable-next-line no-control-regex -- as above.
const UNMAPPED_JOINER = /(?<=[A-Za-z0-9])[\u0000-\u0008\u000e-\u001f�]+(?=[A-Za-z0-9])/g;

type Piece = OcrWord & { eol: boolean };

/**
 * pdf.js splits a line into items wherever the font changes, so one printed
 * word ("79N3ERBX-0026") can arrive as several items that touch. Laying those
 * out separately would put spaces inside the word; joining the ones that touch
 * keeps it whole. A space item or a line end always separates words.
 */
function joinTouching(items: TextItem[], viewport: pdfjsLib.PageViewport): Piece[] {
  const pieces: Piece[] = [];
  let open: Piece | undefined;

  for (const item of items) {
    if (!item.str.trim()) {
      // An empty item flagged hasEOL ends the line before it.
      const last = pieces.at(-1);
      if (item.hasEOL && last) last.eol = true;
      open = undefined;
      continue;
    }

    // PDF space runs bottom-up; the viewport transform turns it into page pixels.
    const [, , c, d, x, baseline] = multiply(viewport.transform, item.transform);
    const height = Math.hypot(c, d);
    const x1 = x + item.width * viewport.scale;
    const touches =
      open &&
      Math.abs(open.y1 - baseline) <= height * TOUCHING &&
      Math.abs(x - open.x1) <= height * TOUCHING;

    if (open && touches) {
      open.text += item.str;
      open.x1 = x1;
      open.y0 = Math.min(open.y0, baseline - height);
    } else {
      open = { text: item.str, x0: x, x1, y0: baseline - height, y1: baseline, eol: false };
      pieces.push(open);
    }
    if (item.hasEOL) {
      open.eol = true;
      open = undefined;
    }
  }

  // Between two letters or digits an unmapped glyph is a joiner ("79N3ERBX-0026");
  // anywhere else (a bracket, a trailing dash) it is dropped.
  for (const piece of pieces) {
    piece.text = piece.text.replace(UNMAPPED_JOINER, '-').replace(UNMAPPED, '');
  }
  return pieces;
}

type TextItem = Extract<
  Awaited<ReturnType<pdfjsLib.PDFPageProxy['getTextContent']>>['items'][number],
  { str: string }
>;

/** 2D affine transforms as PDF writes them: [a, b, c, d, e, f]. */
type Matrix = [number, number, number, number, number, number];

function multiply(m1: number[], m2: number[]): Matrix {
  const [a1 = 0, b1 = 0, c1 = 0, d1 = 0, e1 = 0, f1 = 0] = m1;
  const [a2 = 0, b2 = 0, c2 = 0, d2 = 0, e2 = 0, f2 = 0] = m2;
  return [
    a1 * a2 + c1 * b2,
    b1 * a2 + d1 * b2,
    a1 * c2 + c1 * d2,
    b1 * c2 + d1 * d2,
    a1 * e2 + c1 * f2 + e1,
    b1 * e2 + d1 * f2 + f1,
  ];
}

async function extractFromImage(file: File): Promise<OCRResult> {
  const worker = await createOCRWorker();

  try {
    const page = await recognizePage(worker, file, 1);

    return {
      text: page.text,
      pages: [page],
    };
  } finally {
    await worker.terminate();
  }
}

async function extractFromPDF(file: File): Promise<OCRResult> {
  const arrayBuffer = await file.arrayBuffer();

  const pdf = await pdfjsLib.getDocument({
    data: arrayBuffer,
  }).promise;

  // Guide §15 "File Size" — page limit.
  if (pdf.numPages > MAX_PDF_PAGES) {
    throw new Error(`This PDF has ${pdf.numPages} pages. The limit is ${MAX_PDF_PAGES} pages.`);
  }

  // Started on the first page that needs OCR, then reused for the rest: a
  // digital PDF never loads the OCR engine at all.
  let worker: OCRWorker | undefined;

  const pages: OCRResult['pages'] = [];

  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);

      // Scale 2 is a practical starting point for invoice OCR.
      // Higher values may improve recognition but increase CPU/RAM usage.
      const viewport = page.getViewport({
        scale: 2,
      });

      const textLayer = await readTextLayer(page, viewport, pageNumber);
      if (textLayer) {
        pages.push(textLayer);
        continue;
      }

      worker ??= await createOCRWorker();

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error(`Unable to create canvas context for page ${pageNumber}`);
      }

      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);

      await page.render({
        canvasContext: context,
        viewport,
        canvas,
      }).promise;

      pages.push(await recognizePage(worker, canvas, pageNumber));
    }
  } finally {
    await worker?.terminate();
  }

  return {
    text: pages.map((page) => page.text).join('\n\n'),
    pages,
  };
}

// Guide §15 "Security" and "File Size" — checked before any OCR work starts.
function validateInvoiceFile(file: File): void {
  const extensions = SUPPORTED_EXTENSIONS[file.type];
  const name = file.name.toLowerCase();

  if (!extensions || !extensions.some((extension) => name.endsWith(extension))) {
    throw new Error(`Unsupported invoice file type: ${file.type || file.name}`);
  }

  const limit = file.type === 'application/pdf' ? MAX_PDF_BYTES : MAX_IMAGE_BYTES;
  if (file.size > limit) {
    throw new Error(
      `${file.name} is ${(file.size / MB).toFixed(1)} MB. The limit is ${limit / MB} MB.`,
    );
  }
}

export async function extractInvoiceText(file: File): Promise<OCRResult> {
  validateInvoiceFile(file);

  let result: OCRResult;

  if (file.type === 'application/pdf') {
    result = await extractFromPDF(file);
  } else if (file.type.startsWith('image/')) {
    result = await extractFromImage(file);
  } else {
    throw new Error(`Unsupported invoice file type: ${file.type || file.name}`);
  }

  // Guide §17 "Empty OCR result".
  if (!result.text.trim()) {
    throw new Error('No text could be read from this invoice.');
  }

  return result;
}
